// PreventiApp · Sprint 3 · Etapa 7 — Data layer admin (lecturas vía cliente Supabase
// + escrituras sensibles vía FastAPI). Todo lo simple usa RLS; sólo gestión de
// usuarios y reset de password pasan por backend con service_role.
import { supabase } from '../lib/supabase';

const API = process.env.REACT_APP_BACKEND_URL;

// ----------------- Colegios -----------------
export async function listColegios() {
  const { data, error } = await supabase
    .from('colegios')
    .select('id, rbd, nombre, sostenedor, dependencia, region, comuna, estado, plan_contrato, creado_en, permite_soporte_inside_hasta, permite_soporte_inside_motivo')
    .order('nombre');
  if (error) throw error;
  return data || [];
}

export async function updateColegio(id, patch) {
  // INSIDE puede editar via RLS (super_admin_inside o staff con puede_gestionar_colegios)
  const { error } = await supabase.from('colegios').update(patch).eq('id', id);
  if (error) throw error;
}

export async function createColegio(payload) {
  const { data, error } = await supabase.from('colegios').insert(payload).select('id').single();
  if (error) throw error;
  return data.id;
}

// ----------------- Usuarios (Profiles) -----------------
// Lectura: cliente Supabase + RLS. Para producción se recomienda filtrar por colegio_id
// porque los profiles SELECT policy autorizan al colegio sí mismo + INSIDE staff con permiso.
export async function listUsuarios({ colegio_id } = {}) {
  let q = supabase
    .from('profiles')
    .select('id, colegio_id, rol, nombre_completo, rut, correo, telefono_movil, cargo, estado, requiere_cambio_password, ultimo_login, creado_en, es_inside_staff, permisos_inside')
    .order('nombre_completo');
  if (colegio_id) q = q.eq('colegio_id', colegio_id);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

async function _authedFetch(path, opts = {}) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error('Sin sesión activa');
  const headers = { ...(opts.headers || {}), 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const r = await fetch(`${API}${path}`, { ...opts, headers });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
  return j;
}

export const createUsuario = (body) => _authedFetch('/api/admin/users/create', { method: 'POST', body: JSON.stringify(body) });
export const updateUsuario = (id, patch) => _authedFetch(`/api/admin/users/${id}/update`, { method: 'POST', body: JSON.stringify(patch) });
export const resetUsuarioPassword = (id) => _authedFetch(`/api/admin/users/${id}/reset_password`, { method: 'POST' });

// ----------------- Cursos -----------------
export async function listCursos({ colegio_id } = {}) {
  let q = supabase
    .from('cursos')
    .select('id, colegio_id, anio, nivel, letra, profesor_jefe_id, estado, creado_en')
    .order('anio', { ascending: false })
    .order('nivel')
    .order('letra');
  if (colegio_id) q = q.eq('colegio_id', colegio_id);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function createCurso(payload) {
  const { data, error } = await supabase.from('cursos').insert(payload).select('id').single();
  if (error) throw error;
  return data.id;
}

export async function updateCurso(id, patch) {
  const { error } = await supabase.from('cursos').update(patch).eq('id', id);
  if (error) throw error;
}

// ----------------- Observaciones (lista + detalle según RLS) -----------------
export async function listObservaciones({ colegio_id, modo, estado, dominio_code, limit = 200 } = {}) {
  let q = supabase
    .from('observaciones')
    .select('id, colegio_id, autor_id, dominio_code, conducta_code, curso_texto, estudiante, modo_registro, estado, flujo_captura, creado_en, actualizado_en')
    .order('creado_en', { ascending: false })
    .limit(limit);
  if (colegio_id) q = q.eq('colegio_id', colegio_id);
  if (modo) q = q.eq('modo_registro', modo);
  if (estado) q = q.eq('estado', estado);
  if (dominio_code) q = q.eq('dominio_code', dominio_code);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function getObservacion(id) {
  const { data, error } = await supabase
    .from('observaciones')
    .select(`
      id, colegio_id, autor_id, dominio_code, conducta_code, curso_texto, estudiante,
      contexto, frecuencia, intensidad, duracion, participantes, fuente,
      modo_registro, estado, flujo_captura, creado_en, actualizado_en,
      observaciones_evidencias ( id, tipo, storage_path, contenido_texto, mime, segundos, creado_en )
    `)
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

export async function listVersionesObs(observacion_id) {
  const { data, error } = await supabase
    .from('observaciones_versions')
    .select('id, accion, creado_en, actor_id, snapshot')
    .eq('observacion_id', observacion_id)
    .order('creado_en', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function cambiarEstadoObservacion(id, nuevo_estado) {
  const { error } = await supabase.from('observaciones').update({ estado: nuevo_estado }).eq('id', id);
  if (error) throw error;
}

export async function getFirmaFoto(storage_path, expires = 300) {
  const { data, error } = await supabase.storage.from('evidencias').createSignedUrl(storage_path, expires);
  if (error) throw error;
  return data?.signedUrl;
}

// ----------------- Profile lookup (para nombres en listas) -----------------
export async function listProfileNames(ids) {
  const unique = Array.from(new Set(ids || [])).filter(Boolean);
  if (unique.length === 0) return {};
  const { data, error } = await supabase.from('profiles').select('id, nombre_completo, rol').in('id', unique);
  if (error) throw error;
  const map = {};
  for (const p of data || []) map[p.id] = p;
  return map;
}

// ----------------- Soporte INSIDE -----------------
export async function getSoporteInfo(colegio_id) {
  const { data, error } = await supabase
    .from('colegios')
    .select('id, nombre, permite_soporte_inside_hasta, permite_soporte_inside_motivo, permite_soporte_inside_creado_en, permite_soporte_inside_por')
    .eq('id', colegio_id)
    .single();
  if (error) throw error;
  return data;
}

export async function otorgarSoporte(colegio_id, { motivo, vigente_hasta, actor_id }) {
  // El INSERT en colegios_soporte_inside_log lo hace AUTO el trigger DB
  // tg_colegios_soporte_log cuando cambia permite_soporte_inside_hasta.
  const patch = {
    permite_soporte_inside_hasta: vigente_hasta,
    permite_soporte_inside_motivo: motivo,
    permite_soporte_inside_por: actor_id,
    permite_soporte_inside_creado_en: new Date().toISOString(),
  };
  const { error } = await supabase.from('colegios').update(patch).eq('id', colegio_id);
  if (error) throw error;
}

export async function revocarSoporte(colegio_id) {
  // Idem: el log de revocación lo emite el trigger DB.
  const patch = {
    permite_soporte_inside_hasta: null,
    permite_soporte_inside_motivo: null,
    permite_soporte_inside_por: null,
    permite_soporte_inside_creado_en: null,
  };
  const { error } = await supabase.from('colegios').update(patch).eq('id', colegio_id);
  if (error) throw error;
}

export async function listSoporteLog(colegio_id) {
  const { data, error } = await supabase
    .from('colegios_soporte_inside_log')
    .select('id, accion, vigente_hasta, autorizado_por, motivo, creado_en')
    .eq('colegio_id', colegio_id)
    .order('creado_en', { ascending: false });
  if (error) throw error;
  return data || [];
}
